import { GripVertical } from 'lucide-react'
import React from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { CheckboxWithLabel } from '@/components/lemmala-design-system/checkbox/checkbox'
import { CommandItem } from '@/components/lemmala-design-system/command/command'
import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'

import { NESTED_ITEM_PADDING } from './consts'
import { DropdownSelectContext } from './dropdown-select-context'
import { DropdownSelectItemContent } from './dropdown-select-item-content'
import { DropdownSelectItem } from './dropdown-select.types'

import CheckIcon from '@/assets/dropdown-check.svg'

interface DropdownSelectItemComponentProps {
  item: DropdownSelectItem
  /** How many parents the item is listed under. */
  depth?: number
}

export const DropdownSelectItemComponent = React.memo(
  ({ item, depth = 0 }: DropdownSelectItemComponentProps) => {
    const context = React.useContext(DropdownSelectContext)

    if (!context) {
      throw new Error('DropdownSelectItemComponent must be used within DropdownSelect')
    }

    const {
      multi,
      draggable,
      selectedItems = [],
      partiallySelectedItems,
      onItemSelect,
      onItemToggle,
      showTooltips,
    } = context

    const isSelected = selectedItems.includes(item.id)
    const isPartiallySelected = partiallySelectedItems?.has(item.id) ?? false

    const handleClick = () => {
      if (item.disabled) return

      if (multi && onItemToggle) {
        onItemToggle(item.id, !isSelected)
      } else if (onItemSelect) {
        onItemSelect(item.id)
      }
    }

    return (
      <CommandItem
        value={item.id}
        disabled={item.disabled}
        onMouseDown={(e) => {
          e.preventDefault()
          if (!item.disabled) {
            handleClick()
          }
        }}
        className={cn(
          'cursor-pointer text-body-default',
          draggable ? 'px-0.5' : 'px-2',
          depth > 0 && NESTED_ITEM_PADDING[Math.min(depth, NESTED_ITEM_PADDING.length) - 1],
          !item.disabled && 'hover:!bg-main-background-interactive-list-hover',
          isSelected &&
            !multi &&
            'bg-main-background-interactive-list-selected hover:!bg-main-background-interactive-list-selectedHover',
          item.disabled && 'text-main-foreground-disabled opacity-50',
        )}
      >
        {draggable && (
          <GripVertical
            className={cn(
              'mr-1.5 size-4 shrink-0',
              item.disabled ? 'text-main-foreground-disabled' : 'text-main-foreground-tertiary',
            )}
          />
        )}

        {multi ? (
          <CheckboxWithLabel
            disabled={item.disabled}
            checked={isSelected || isPartiallySelected}
            indeterminate={isPartiallySelected}
            className='text-label-default'
          >
            <Tooltip
              trigger={<DropdownSelectItemContent item={item} />}
              tooltipDisabled={!showTooltips}
            >
              {item.value || item.label}
            </Tooltip>
          </CheckboxWithLabel>
        ) : (
          <div className='flex w-full items-center justify-between'>
            <Tooltip
              trigger={<DropdownSelectItemContent item={item} />}
              tooltipDisabled={!showTooltips}
            >
              {item.value || item.label}
            </Tooltip>
            {isSelected && <CheckIcon className='size-4 text-main-foreground-accent' />}
          </div>
        )}
      </CommandItem>
    )
  },
)
DropdownSelectItemComponent.displayName = 'DropdownSelectItemComponent'
